import clsx from 'clsx';
import { type JSX, useContext, useRef } from 'react';
import { useIntl } from 'react-intl';

import Colors from '@dynatrace/strato-design-tokens/colors';
import { MinusIcon, PlusIcon } from '@dynatrace/strato-icons';

import { SubrowCellContext } from './SubRowCellContext.js';
import { SubRowExpanderPath } from './SubRowExpanderPath.js';
import * as styles from './SubRows.sty.js';
import { getSubRowCellWidth } from './SubRows.js';
import {
  DATA_TABLE_SUB_ROW_EXPANDER_ICON_SIZE as SUB_ROW_EXPANDER_ICON_SIZE,
  DATA_TABLE_SUB_ROW_INDENT_GRID as SUB_ROW_INDENT_GRID,
} from '../../constants.js';
import {
  useDataTable,
  useDataTableRow,
} from '../../contexts/DataTableContext.js';
import type { Table } from '../../hooks/useTable/types.js';
import { dataTableMessages } from '../../messages.js';
import {
  isChartColumnType,
  type DataTableColumnType,
} from '../ColumnTypes/column-types-types.js';

function remeasureSubRowIndicatorColumn(table: Table<unknown>) {
  const subrowIndicatorColumnId = table.getSubRowIndicatorColumnId();
  if (subrowIndicatorColumnId) {
    table.remeasureColumn(subrowIndicatorColumnId);
  }
}

/**
 * @internal
 */
export function SubRowsHeaderExpander(
  props: Readonly<{ isAllRowsExpanded: boolean }>,
): JSX.Element {
  const { isAllRowsExpanded } = props;
  const table = useDataTable();
  const intl = useIntl();

  return (
    <button
      type="button"
      className={clsx(
        styles.expandButtonHeaderCellWrapper,
        styles.expanderButton,
      )}
      onClick={() => {
        table.toggleAllRowsExpanded();
        remeasureSubRowIndicatorColumn(table);
      }}
      aria-label={intl.formatMessage(dataTableMessages.subRowAllTrigger, {
        expandState: isAllRowsExpanded ? 'other' : 'expand',
      })}
      style={{
        color: Colors.Text.Neutral.Subdued,
      }}
      data-dt-name="SubRowExpandCollapseAll"
      data-dt-properties={`state:${isAllRowsExpanded ? 'collapse' : 'expand'}`}
    >
      {isAllRowsExpanded ? <MinusIcon /> : <PlusIcon />}
    </button>
  );
}

/**
 * @internal
 */
export function SubRowsCellExpander({
  columnType,
}: {
  columnType: DataTableColumnType | undefined;
}): JSX.Element | null {
  const intl = useIntl();
  const table = useDataTable();
  const row = useDataTableRow();

  const subrowContext = useContext(SubrowCellContext);
  const subRowExpandButtonWrapperRef = useRef<HTMLDivElement>(null);

  if (!row) {
    return null;
  }

  const {
    canExpand: rowCanExpand,
    canExpandDisabled: rowCanExpandDisabled,
    isExpanded,
  } = subrowContext;

  const indentWidth = SUB_ROW_INDENT_GRID * row.depth;
  const shouldCorrectIndicatorWidth = isChartColumnType(columnType);

  const subRowDepth = table.getSubRowOpenDepth();
  const depthDifference = subRowDepth - row.depth;
  const widthCorrection = SUB_ROW_INDENT_GRID * depthDifference;
  const rowDensity = table.getVisualVariantOptions().rowDensity;
  let width = getSubRowCellWidth(rowDensity, indentWidth);
  if (shouldCorrectIndicatorWidth) {
    width = `calc(${width} + ${widthCorrection}px)`;
  }

  return (
    <div
      ref={subRowExpandButtonWrapperRef}
      className={clsx(styles.expandButtonCellWrapper)}
      style={{
        width,
      }}
    >
      {row.depth > 0 ? (
        <SubRowExpanderPath
          rowId={row.id}
          width={indentWidth}
          rowLineConfig={table.getRowLineConfig(row.id)}
          rowCanExpand={rowCanExpand}
        />
      ) : null}

      {/* Only render a button if the row can actually expand */}
      {rowCanExpand ? (
        <button
          disabled={rowCanExpandDisabled}
          type="button"
          aria-label={intl.formatMessage(dataTableMessages.subRowTrigger, {
            expandState: isExpanded ? 'other' : 'expand',
            id: row.id,
          })}
          aria-expanded={isExpanded}
          className={styles.expanderButton}
          onClick={() => {
            row.toggleExpanded(!isExpanded);
            remeasureSubRowIndicatorColumn(table);
          }}
          data-dt-name="SubRowExpandCollapse"
          data-dt-properties={`state:${isExpanded ? 'collapse' : 'expand'}`}
        >
          {isExpanded ? (
            <MinusIcon size={SUB_ROW_EXPANDER_ICON_SIZE} />
          ) : (
            <PlusIcon size={SUB_ROW_EXPANDER_ICON_SIZE} />
          )}
        </button>
      ) : null}

      {shouldCorrectIndicatorWidth ? (
        <div style={{ width: widthCorrection }} />
      ) : null}
    </div>
  );
}
